import { useState, useEffect } from 'react';
import { useCountry } from '../../contexts/CountryContext';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { BarChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { BarChart3, TrendingUp, FileText, AlertTriangle } from 'lucide-react';

const COLORS = ['#1a4d4d', '#2e7d32', '#43a047', '#66bb6a', '#a5d6a7'];

export function AnalyticsPage() {
  const { selectedCountry } = useCountry();
  const [collectionStats, setCollectionStats] = useState(null);
  const [exceptionTrends, setExceptionTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedCountry ? { country_code: selectedCountry } : {};
      const [statsData, trendsData] = await Promise.all([
        api.getCollectionAnalytics(params),
        api.getExceptionTrends(params),
      ]);
      setCollectionStats(statsData);
      setExceptionTrends(trendsData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
        <p className="text-muted-foreground mt-1">
          Performance reports and operational insights
        </p>
      </div>

      {/* Collection Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Scheduled</p>
                <p className="text-3xl font-bold">{collectionStats?.total_scheduled || 0}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-lg">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold text-green-600">{collectionStats?.completed || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Missed</p>
                <p className="text-3xl font-bold text-red-600">{collectionStats?.missed || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completion Rate</p>
                <p className="text-3xl font-bold text-primary">{collectionStats?.completion_rate || 0}%</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exception Trends Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Exception Trends (Last 6 Months)</CardTitle>
        </CardHeader>
        <CardContent>
          {exceptionTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={exceptionTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Critical" />
                <Bar dataKey="high" stackId="a" fill="#f59e0b" name="High" />
                <Bar dataKey="other" stackId="a" fill="#3b82f6" name="Other" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-muted-foreground">No exception data available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports Section */}
      <Card>
        <CardHeader>
          <CardTitle>Available Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'Vendor Performance Report', description: 'Score trends, exceptions, missed collections' },
              { title: 'Country Operations Report', description: 'Stores, vendors, compliance by country' },
              { title: 'Collection Execution Report', description: 'Scheduled vs completed vs missed' },
              { title: 'Cost Summary Report', description: 'Offer values by country and vendor' },
              { title: 'Compliance Expiry Report', description: 'Documents expiring in 90 days' },
            ].map((report, i) => (
              <div
                key={i}
                className="p-4 border border-border rounded-lg hover:border-primary/30 transition-colors cursor-pointer"
              >
                <h3 className="font-medium text-foreground">{report.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{report.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
